/**
 * EarningsTab.tsx — the "Earnings" sub-view of the merged Insights section
 * (#23, 2026-07-21; foldered under Insights in UX Wave 3c). ADMIN-only
 * surface — money IS shown here. Since the CTO decision of 2026-08-26 a
 * clinician may see their OWN earnings on their My-stats card; the company
 * TOTAL and this per-clinician table stay ADMIN-only (Reshape §3.1 continues
 * to cover patient money and other clinicians' figures).
 *
 * Earnings = activity counts × configured pence rates, computed server-side
 * (orders-service /admin/earnings, fail-soft `partial`). The period comes from
 * this tab's OWN month picker (2026-08-26 — this month to date / past 12
 * calendar months / custom day range, serialised as half-open UTC `?from=&to=`
 * via lib/monthRange; the Insights-shared `?days` selector now drives ONLY the
 * Company analytics sub-view, whose reads still speak days). Names resolve
 * through the batched staff lookup. Column totals + the grand total render
 * from integer pence through the app money formatter.
 *
 * THE TABLE IS READ-ONLY (2026-09-06). It used to carry SIX live rate inputs
 * and a "Save rates" button on every row, so an admin who only wanted to read
 * what someone earned was one stray keystroke — and one mis-aimed click — away
 * from re-pricing a clinician. Rate editing now lives behind the per-row ⋯
 * menu in {@link RatesDialog}; nothing on the table itself can be typed into.
 *
 * ⚠️ THE RATES PUT IS ALL-OR-NOTHING AND MUST STAY THAT WAY. orders-service
 * treats an ABSENT rate key as LEAVE-UNCHANGED (see CompensationRates in
 * api/types.ts — the three/five-key bodies are still accepted for compat), so
 * a form that sent only the fields the admin touched would silently preserve
 * whatever stale value the others held. The dialog therefore always builds the
 * COMPLETE six-key card from its own state, and `earningsTab.spec.tsx` pins it
 * with a `toEqual` on the wire body rather than a `toMatchObject`.
 *
 * ⚠️ KNOWN DEFECT, NOT FIXED HERE — saving a rate RE-PRICES THE PAST. The save
 * reloads the current period, and the server recomputes earned from the rates
 * as they are NOW; there is no effective-dated rate card. Raise a rate while a
 * past month is on screen and that month's "earned" figure changes after the
 * clinician was already paid on the old number. Fixing it needs an
 * orders-service schema + pricing change (rate versions with validity
 * windows), so it is deliberately NOT faked client-side.
 *
 * EXPORT is CSV — one summary row per clinician (counts, rates, the server's
 * earned figure), offered per row from the ⋯ menu and for everyone from the
 * header action. CSV because the app is CSP-locked and carries no spreadsheet
 * library, and Excel opens it natively. It is a SUMMARY, not a line-item
 * breakdown: today's read returns aggregates only, and a true per-order
 * breakdown needs a new orders-service read rather than an invented one here.
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { lookupStaffEmailMap } from '../api/identity';
import { getEarnings, setCompensation } from '../api/orders';
import type { ClinicianEarnings, EarningsResponse } from '../api/types';
import { formatPence } from '../lib/format';
import type { EarningsRange } from '../lib/monthRange';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { DialogShell } from '../ui/ConfirmDialog';
import { Menu, MenuItem } from '../ui/Menu';
import { MonthRangePicker, defaultEarningsRange } from '../ui/MonthRangePicker';
import { Alert, Button, Card, ErrorState, Field, Help, TextInput } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';

/** pence → an editable pounds string (e.g. 500 → "5.00"). */
const toPounds = (pence: number): string => (pence / 100).toFixed(2);
/** pounds string → integer pence (e.g. "5" → 500); NaN/blank → 0. */
const toPence = (pounds: string): number => Math.max(0, Math.round((parseFloat(pounds) || 0) * 100));

type Rates = {
  perCallbackPence: string;
  perApprovedOrderPence: string;
  perConvertedCallbackPence: string;
  perDispatchPence: string;
  perRepeatRxPence: string;
  perNewPatientRepeatPence: string;
};

/**
 * The six activity counts, in table order — one source for the head, the rows,
 * the totals and the export. These are the EARNED measures: each is paid at a
 * rate and sums into the server's `earnedPence`. The estimate pair
 * (`plansAwaitingPayment` / `estimatedPence`) is deliberately NOT in this list
 * — see ESTIMATE_RULE — so nothing that iterates "what was earned" can pick it
 * up by accident.
 */
const COUNT_FIELDS: Array<{ field: keyof Pick<ClinicianEarnings, 'callbacksHandled' | 'convertedCallbacks' | 'approvedOrders' | 'dispatchedOrders' | 'repeatPrescriptions' | 'newPatientRepeats'>; heading: string }> = [
  { field: 'callbacksHandled', heading: 'Callbacks' },
  { field: 'convertedCallbacks', heading: 'Converted' },
  { field: 'approvedOrders', heading: 'Forms approved' },
  { field: 'dispatchedOrders', heading: 'Deliveries' },
  { field: 'repeatPrescriptions', heading: 'Repeat months' },
  { field: 'newPatientRepeats', heading: 'New patient repeats' },
];

/**
 * The six rates, in dialog order (drives the inputs, their labels AND the
 * export's rate columns). `hint` is OPTIONAL helper copy rendered as a real
 * sentence UNDER the field's label and wired to the input by aria-describedby.
 *
 * WHY it is visible text and never a `title` tooltip: a tooltip does not
 * survive a print, a screenshot, a touch screen or a keyboard-only read, and
 * this sentence is the only place the RULE behind the rate is written down.
 * Five of the rates say what they are in their own label; only the 2026-08-27
 * new-patient one-off needs the rule spelling out, because "per new patient"
 * reads like a rate per signup rather than a once-ever payment.
 */
const RATE_FIELDS: Array<{ field: keyof Rates; label: string; hint?: string }> = [
  { field: 'perCallbackPence', label: 'Rate per callback' },
  { field: 'perApprovedOrderPence', label: 'Rate per approved order' },
  { field: 'perConvertedCallbackPence', label: 'Rate per converted callback' },
  { field: 'perDispatchPence', label: 'Rate per delivery' },
  { field: 'perRepeatRxPence', label: 'Rate per repeat month' },
  {
    field: 'perNewPatientRepeatPence',
    label: "Rate per new patient's first repeat",
    hint: 'A one-off, paid the first time a new patient gets a repeat prescription from their initial form.',
  },
];

/**
 * THE ESTIMATE (CTO 2026-09-09), written out once for the table's help line and
 * the CSV reader alike. The approval fee belongs to whoever gives the FINAL
 * approval before the card is charged, so a plan-link recommendation earns
 * nothing until the patient pays — but the work is done and the fee is
 * expected "until the patient requests something else". The server counts the
 * plans still waiting on the patient and prices them at the clinician's own
 * approval rate; this surface renders that as its OWN figure, beside Earned
 * and never inside it. Column headings: "Awaiting payment" (the count) and
 * "Estimated" (the money).
 */
const ESTIMATE_RULE =
  'Estimated is as of today, not the period above: everything a clinician has recommended that the patient has not paid for yet, ' +
  'whenever it was recommended, priced at their approval rate. It becomes earned when the patient pays and the final approval is given, ' +
  'and changes if the patient asks for something else. It is shown beside the earned figures and is never added to them.';

/**
 * THE ESTIMATE PAIR, RENDERED SAFELY — "—" when the server did not send it.
 *
 * ⚠️ NOT COSMETIC, AND NOT `?? 0`. The types declare `plansAwaitingPayment` and
 * `estimatedPence` as required, but TypeScript cannot enforce a wire contract:
 * a portal running against an orders-service that predates the estimate (a
 * rollback, or a release that lands out of order) receives neither, and
 * `formatPence(undefined)` renders the literal **£NaN** on a money screen —
 * which is what this guard exists to stop. It was live for one deploy: an
 * incomplete inline test fixture hid it behind a React warning nobody read.
 *
 * A zero fallback is the tempting fix and is WORSE: "£0" is indistinguishable
 * from "nothing is outstanding", which is precisely the misreading the
 * as-of-today rule was introduced to end. "—" says we were not told, which is
 * the truth, and it can never be mistaken for a figure.
 */
const estimateText = (value: number | undefined, render: (n: number) => string): string =>
  typeof value === 'number' && Number.isFinite(value) ? render(value) : '—';

/** The stored pence card → the dialog's pounds strings. */
function ratesToPounds(rates: ClinicianEarnings['rates']): Rates {
  return {
    perCallbackPence: toPounds(rates.perCallbackPence),
    perApprovedOrderPence: toPounds(rates.perApprovedOrderPence),
    perConvertedCallbackPence: toPounds(rates.perConvertedCallbackPence),
    perDispatchPence: toPounds(rates.perDispatchPence),
    perRepeatRxPence: toPounds(rates.perRepeatRxPence),
    perNewPatientRepeatPence: toPounds(rates.perNewPatientRepeatPence),
  };
}

/* ── CSV export ─────────────────────────────────────────────────────────────
 * Exported for direct unit testing: the escaping below is a security control,
 * not formatting, and it deserves assertions that do not go through the DOM. */

/** A cell that came from data (a name/email): quoted, and neutered for Excel. */
function csvText(raw: string): string {
  // SMUGGLED SPREADSHEET FORMULAS (CWE-1236). Excel/Sheets EXECUTE a cell that
  // begins = + - @ (or a leading tab/CR), so a staff display name of `=cmd|...`
  // becomes code the moment finance opens the payroll file. A leading
  // apostrophe forces the cell to text — the OWASP remedy — and is stripped on
  // display. (The word for this attack is itself an R28-banned term, hence the
  // long way round: see src/journey/r28.spec.tsx.)
  const guarded = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/**
 * Money as a bare decimal — "47.00", never "£47". A currency glyph would make
 * Excel read the column as TEXT, so the first thing anyone did with the file
 * (sum it) would silently return zero.
 */
const csvPounds = (pence: number): string => (pence / 100).toFixed(2);

/**
 * The summary export: a header row, one row per clinician, and (when a whole
 * period is exported) the server's own grand total as a final row.
 *
 * `from`/`to` ride as the first TWO COLUMNS of every row rather than as a
 * preamble. A preamble line is prettier but it stops the file being one clean
 * table, and the filename — the other obvious home for the window — is the
 * first thing lost when a payroll CSV is renamed and forwarded. A row of this
 * file must be able to say, on its own, which period it is claiming.
 *
 * NO per-component subtotals (count × rate). See the module doc: the server
 * owns the money, and with no effective-dated rate card a client-side
 * `count × rate` can legitimately disagree with the server's `earnedPence` for
 * a past period — a payroll document that does not add up to itself is worse
 * than one that makes the reader do the multiplication.
 */
export function buildEarningsCsv(
  rows: ClinicianEarnings[],
  nameOf: (clinicianRef: string) => string,
  range: EarningsRange,
  totalPence?: number,
  estimatedTotalPence?: number,
): string {
  // The headings go through the same escaper as the data. None of them needs
  // it today; the point is that a label gaining a comma later cannot silently
  // shift every column in the file one place to the right.
  const header = [
    'Period from',
    'Period to',
    'Clinician',
    ...COUNT_FIELDS.map((c) => c.heading),
    ...RATE_FIELDS.map((r) => `${r.label} (£)`),
    'Earned (£)',
    // The estimate pair sits AFTER Earned, so the payroll columns read exactly
    // as they did and the forecast is visibly a separate thing (ESTIMATE_RULE).
    // "(now)" is load-bearing in a file whose every other column IS the period
    // named in the first two cells: this pair is as-of-export, not for that
    // period, and a payroll CSV must not let the two be read as one thing.
    'Plans awaiting payment (now)',
    'Estimated (£, now)',
  ].map(csvText);
  const body = rows.map((c) => [
    csvText(range.from),
    csvText(range.to),
    csvText(nameOf(c.clinicianRef)),
    ...COUNT_FIELDS.map((f) => String(c[f.field])),
    ...RATE_FIELDS.map((f) => csvPounds(c.rates[f.field])),
    csvPounds(c.earnedPence),
    // A server that sent neither writes "—" in both cells, never "NaN" — the
    // same marker the screen uses, so one export cannot describe an absence two
    // ways (see estimateText). The TOTAL row's blank is a different thing: no
    // total is offered for a count column, healthy or not.
    estimateText(c.plansAwaitingPayment, String),
    estimateText(c.estimatedPence, csvPounds),
  ]);
  const lines = [header, ...body];
  if (totalPence !== undefined) {
    // The SERVER's total, re-rendered — never a client re-add of the column
    // above it (the same rule the on-screen Totals row follows).
    lines.push([
      csvText(range.from),
      csvText(range.to),
      'Total',
      ...COUNT_FIELDS.map(() => ''),
      ...RATE_FIELDS.map(() => ''),
      csvPounds(totalPence),
      '',
      // The server's estimated total, on the SAME row and in its OWN column —
      // never folded into the cell to its left.
      estimatedTotalPence === undefined ? '' : estimateText(estimatedTotalPence, csvPounds),
    ]);
  }
  // CRLF per RFC 4180 — Excel's own dialect, and harmless everywhere else.
  return lines.map((cells) => cells.join(',')).join('\r\n');
}

/** A clinician name/period → a filesystem-safe stem. */
const slug = (raw: string): string => raw.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'export';

/**
 * Hand the CSV to the browser as a download. Mirrors api/pharmacy's
 * `openPrescriptionPdf` — a blob: object URL behind a synthetic anchor — which
 * is the pattern already proven against the production CSP.
 *
 * Returns false rather than throwing when the environment has no
 * `URL.createObjectURL`: the caller surfaces that as an honest error instead of
 * a click that appears to do nothing.
 */
function downloadCsv(filename: string, csv: string): boolean {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return false;
  // The BOM is for Excel on Windows only: without it a UTF-8 name (an accented
  // surname) opens as mojibake. It lives here and not in buildEarningsCsv so
  // the pure function stays assertable as plain text.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  return true;
}

export function EarningsTab() {
  // This month to date until the picker says otherwise (2026-08-26).
  const [range, setRange] = useState<EarningsRange>(() => defaultEarningsRange(new Date()));
  const [data, setData] = useState<EarningsResponse | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // The clinician whose rate card is open in the dialog (null = table only).
  const [editing, setEditing] = useState<ClinicianEarnings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback((window: EarningsRange) => {
    setStatus('loading');
    getEarnings(window)
      .then((res) => {
        setData(res);
        setStatus('ready');
        // Resolve clinician ids → emails (best-effort; raw id shows on failure).
        lookupStaffEmailMap(res.clinicians.map((c) => c.clinicianRef))
          .then((map) => (Object.keys(map).length > 0 ? setNames(map) : undefined))
          .catch(() => undefined);
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    load(range);
  }, [load, range]);

  /** The display name for a ref — the resolved email, else the raw id (never a blank cell). */
  const nameOf = useCallback((clinicianRef: string) => names[clinicianRef] ?? clinicianRef, [names]);

  /**
   * ⚠️ A BLANK FIELD IS NOT A ZERO RATE (found in review, 2026-09-06).
   *
   * `toPence` is `parseFloat(x) || 0`, so an empty box saves £0.00 — and the
   * complete-card PUT below lands it. Clearing a field to retype it, then
   * mis-clicking Save, silently sets that rate to nothing.
   *
   * It was survivable while the rates sat in the table: a zero was visible on
   * the row. This rebuild moved them behind a dialog, so a zeroed rate is now
   * invisible until somebody reopens that clinician's card — and because a save
   * re-prices the period on screen, one stray press can rewrite a month
   * downward with nothing left showing it happened.
   *
   * A blank is REFUSED rather than coerced: £0.00 is a legitimate rate an admin
   * may genuinely want (three of the six ship at zero today), so we cannot tell
   * "meant nothing" from "meant zero" by the value. Only the empty box
   * distinguishes them, and it has to be caught before it becomes a number.
   */
  const blankRateField = (rates: Rates): string | null => {
    for (const { field, label } of RATE_FIELDS) {
      if (rates[field].trim() === '') return label;
    }
    return null;
  };

  function save(clinicianRef: string, rates: Rates) {
    const blank = blankRateField(rates);
    if (blank) {
      setSaveError(`${blank} is empty. Enter a figure — use 0 if the rate really is nothing.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    // THE COMPLETE SIX-KEY CARD, ALWAYS — an omitted key means
    // "leave unchanged" server-side, so a partial body would quietly keep a
    // stale rate the admin believes they have just replaced.
    setCompensation(clinicianRef, {
      perCallbackPence: toPence(rates.perCallbackPence),
      perApprovedOrderPence: toPence(rates.perApprovedOrderPence),
      perConvertedCallbackPence: toPence(rates.perConvertedCallbackPence),
      perDispatchPence: toPence(rates.perDispatchPence),
      perRepeatRxPence: toPence(rates.perRepeatRxPence),
      perNewPatientRepeatPence: toPence(rates.perNewPatientRepeatPence),
    })
      .then(() => {
        setSaving(false);
        setEditing(null);
        load(range); // recompute earned against the new rates
      })
      .catch(() => {
        setSaving(false);
        // The dialog STAYS OPEN on failure — closing it would throw away the
        // numbers the admin just typed, which is the one thing they cannot
        // recover from a read-only table.
        setSaveError('Sorry — the rates could not be saved. Please try again.');
      });
  }

  function exportRows(rows: ClinicianEarnings[], stem: string, totalPence?: number, estimatedTotalPence?: number) {
    setExportError(null);
    const csv = buildEarningsCsv(rows, nameOf, range, totalPence, estimatedTotalPence);
    const period = `${range.from.slice(0, 10)}_to_${range.to.slice(0, 10)}`;
    if (!downloadCsv(`${stem}_${period}.csv`, csv)) {
      setExportError('Sorry — this browser could not start the download.');
    }
  }

  const countCellStyle = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };
  const totalCellStyle = { ...countCellStyle, fontWeight: 700 as const };

  // Client-side COUNT sums over the SERVER-computed rows — display-only; the
  // earned total is never re-added here: the grand total renders the server's
  // `totalPence` (the one money source of truth) in that cell too.
  const totals = (data?.clinicians ?? []).reduce(
    (sum, c) => ({
      callbacksHandled: sum.callbacksHandled + c.callbacksHandled,
      convertedCallbacks: sum.convertedCallbacks + c.convertedCallbacks,
      approvedOrders: sum.approvedOrders + c.approvedOrders,
      dispatchedOrders: sum.dispatchedOrders + c.dispatchedOrders,
      repeatPrescriptions: sum.repeatPrescriptions + c.repeatPrescriptions,
      newPatientRepeats: sum.newPatientRepeats + c.newPatientRepeats,
      // ⚠️ STARTS `undefined`, NOT 0, and that is the whole point. A row that
      // carried no count must not poison the column with NaN — but a 0 seed
      // would collapse "nobody sent a count" and "everybody sent zero" into the
      // same rendered `0`, which is the lying zero `estimateText` exists to
      // stop. Undefined until a row actually supplies one, so the total below
      // reaches the "—" branch exactly when the cells above it do.
      // `typeof` (not a bare Number.isFinite) so the compiler NARROWS: since
      // the field became optional, the type system is what now stops a missing
      // count being added into the total.
      plansAwaitingPayment:
        typeof c.plansAwaitingPayment === 'number' && Number.isFinite(c.plansAwaitingPayment)
          ? (sum.plansAwaitingPayment ?? 0) + c.plansAwaitingPayment
          : sum.plansAwaitingPayment,
    }),
    {
      callbacksHandled: 0,
      convertedCallbacks: 0,
      approvedOrders: 0,
      dispatchedOrders: 0,
      repeatPrescriptions: 0,
      newPatientRepeats: 0,
      plansAwaitingPayment: undefined as number | undefined,
    },
  );
  // Muted on purpose: a forecast must never read with the weight of money owed.
  const estimateCellStyle = { ...countCellStyle, color: 'var(--gps-muted)' };

  return (
    <Card>
      <SectionTitle>Clinician earnings</SectionTitle>

      {/* This tab's OWN period picker (2026-08-26) with the export action and
          the grand total beside it — the Insights-shared days selector drives
          only Company analytics. */}
      <div style={{ display: 'flex', gap: 'var(--gps-s-2)', alignItems: 'center', marginBottom: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
        <MonthRangePicker idBase="earnings-period" onRange={setRange} />
        {data && status === 'ready' && data.clinicians.length > 0 ? (
          <>
            <Button
              variant="ghost"
              onClick={() => exportRows(data.clinicians, 'clinician-earnings', data.totalPence, data.estimatedTotalPence)}
              style={{ width: 'auto', minHeight: '36px', padding: '6px 14px', fontSize: '13px', marginLeft: 'auto' }}
            >
              Export all (CSV)
            </Button>
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)' }}>Total: {formatPence(data.totalPence)}</span>
            {/* The forecast, BESIDE the total — its own figure, its own label,
                muted, and never added into the number to its left. */}
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gps-muted)' }}>
              Estimated now: {estimateText(data.estimatedTotalPence, formatPence)}
            </span>
          </>
        ) : null}
      </div>

      {status === 'loading' ? (
        // Wave 6 §1: row-shaped loading — the earnings table's columns as rows.
        <TableSkeleton rows={3} columns={10} label="Loading earnings…" />
      ) : status === 'error' || !data ? (
        <ErrorState message="Sorry — we could not load earnings." onRetry={() => load(range)} />
      ) : (
        <>
          {data.partial ? (
            <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {/* ⚠️ THE ESTIMATE COMES DOWN THE SAME CLINIC READ AS THE CALLBACK
                  COUNTS. The old wording said "order figures are still exact",
                  which was true of the four order-anchored rates and NOT of the
                  estimate — so a reader saw "—" in a column the banner had just
                  vouched for. Name what is affected, and what genuinely is not. */}
              Callback figures are temporarily unavailable. Callbacks and converted are counted as zero, so earnings are
              understated, and the estimate cannot be worked out at all — it shows as “—” rather than a figure. The order
              figures (forms approved, deliveries, repeat months, new patient repeats) are still exact.
            </Alert>
          ) : null}
          {exportError ? (
            <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {exportError}
            </Alert>
          ) : null}

          {data.clinicians.length === 0 ? (
            <Help style={{ marginBottom: 0 }}>No clinician activity or rates configured for this window yet.</Help>
          ) : (
            <DataTable
              label="Clinician earnings"
              head={
                <>
                  <Th>Clinician</Th>
                  {COUNT_FIELDS.map((c) => (
                    <Th key={c.field} style={{ textAlign: 'right' }}>
                      {c.heading}
                    </Th>
                  ))}
                  <Th style={{ textAlign: 'right' }}>Earned</Th>
                  {/* The estimate pair — AFTER Earned, so the six paid
                      measures and their total read exactly as before and the
                      forecast is visibly separate (ESTIMATE_RULE below).
                      ⚠️ These two do NOT move with the period picker: they are
                      what is outstanding TODAY (CTO 2026-09-09). The rule
                      sentence under the table is where that is stated. */}
                  {/* "(now)" for the same reason the CSV headers carry it:
                      every OTHER column here is the period named in the picker
                      directly above, and these two are not. */}
                  <Th style={{ textAlign: 'right' }}>Awaiting payment (now)</Th>
                  <Th style={{ textAlign: 'right' }}>Estimated (now)</Th>
                  {/* The ⋯ column — labelled for screen readers only (an empty
                      header cell fails the axe empty-table-header rule, caught
                      by the Wave-3c Insights scan). */}
                  <Th>
                    <span className="gps-sr-only">Actions</span>
                  </Th>
                </>
              }
            >
              {data.clinicians.map((c) => (
                <tr key={c.clinicianRef}>
                  <Td>{nameOf(c.clinicianRef)}</Td>
                  {COUNT_FIELDS.map((f) => (
                    <Td key={f.field} style={countCellStyle}>
                      {c[f.field]}
                    </Td>
                  ))}
                  <Td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatPence(c.earnedPence)}
                  </Td>
                  <Td style={estimateCellStyle}>{estimateText(c.plansAwaitingPayment, String)}</Td>
                  <Td style={{ ...estimateCellStyle, whiteSpace: 'nowrap' }}>{estimateText(c.estimatedPence, formatPence)}</Td>
                  <Td style={{ textAlign: 'right' }}>
                    <Menu menuLabel={`Actions for ${nameOf(c.clinicianRef)}`} triggerLabel={`Actions for ${nameOf(c.clinicianRef)}`}>
                      {({ close }) => (
                        <>
                          <MenuItem
                            onSelect={() => {
                              close();
                              exportRows([c], `earnings-${slug(nameOf(c.clinicianRef))}`);
                            }}
                          >
                            Export earnings breakdown…
                          </MenuItem>
                          <MenuItem
                            onSelect={() => {
                              close();
                              setSaveError(null);
                              setEditing(c);
                            }}
                          >
                            Update rates…
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </Td>
                </tr>
              ))}
              {/* Column totals (2026-08-26): count sums client-side for display;
                  the earned column re-renders the SERVER total — never a second,
                  possibly-drifting money computation. */}
              <tr>
                <Td style={{ fontWeight: 700 }}>Totals</Td>
                {COUNT_FIELDS.map((f) => (
                  <Td key={f.field} style={totalCellStyle}>
                    {totals[f.field]}
                  </Td>
                ))}
                <Td style={{ ...totalCellStyle, whiteSpace: 'nowrap' }}>{formatPence(data.totalPence)}</Td>
                {/* The count sums client-side like the others; the money is
                    the SERVER's estimatedTotalPence — and it stays in its own
                    cell, never added into the total to its left. */}
                <Td style={estimateCellStyle}>{estimateText(totals.plansAwaitingPayment, String)}</Td>
                <Td style={{ ...estimateCellStyle, whiteSpace: 'nowrap' }}>{estimateText(data.estimatedTotalPence, formatPence)}</Td>
                <Td />
              </tr>
            </DataTable>
          )}
          {data.clinicians.length > 0 ? (
            // A REAL SENTENCE under the table, visible — the only written record
            // of what the two right-hand columns mean and why they are not money.
            <p style={{ margin: 'var(--gps-s-2) 0 0', fontSize: '12.5px', lineHeight: 1.45, color: 'var(--gps-muted)' }}>{ESTIMATE_RULE}</p>
          ) : null}
        </>
      )}

      {editing ? (
        <RatesDialog
          // Keyed by ref so opening a DIFFERENT clinician remounts with their
          // own stored rates rather than inheriting the last one's edits.
          key={editing.clinicianRef}
          clinicianName={nameOf(editing.clinicianRef)}
          initial={ratesToPounds(editing.rates)}
          busy={saving}
          error={saveError}
          onSave={(rates) => save(editing.clinicianRef, rates)}
          onCancel={() => {
            setSaveError(null);
            setEditing(null);
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * The per-clinician rate card, in a modal. Deliberately JUST the six rates:
 * no counts and no "14 × £12" arithmetic — an admin re-pricing someone should
 * be reading the rate card, not doing sums, and the sums belong in the export
 * where they can be checked against the server's own earned figure.
 *
 * Rates are stored PER CLINICIAN (`setCompensation(ref, …)`); that they all
 * hold the same numbers today is a fact about the data, not about the API, so
 * the dialog is titled and saved per person.
 */
function RatesDialog({
  clinicianName,
  initial,
  busy,
  error,
  onSave,
  onCancel,
}: {
  clinicianName: string;
  initial: Rates;
  busy: boolean;
  error: string | null;
  onSave: (rates: Rates) => void;
  onCancel: () => void;
}) {
  const [rates, setRates] = useState<Rates>(initial);
  const fieldId = useId();

  return (
    <DialogShell title={`Update rates — ${clinicianName}`} onCancel={busy ? () => {} : onCancel} width="min(480px, 100%)">
      <form
        aria-label={`Update rates for ${clinicianName}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) onSave(rates);
        }}
      >
        {error ? (
          <Alert tone="warning" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {error}
          </Alert>
        ) : null}

        {RATE_FIELDS.map((f) => {
          const inputId = `${fieldId}-${f.field}`;
          const hintId = `${inputId}-hint`;
          return (
            <Field key={f.field} label={f.label} htmlFor={inputId}>
              {/* A REAL SENTENCE, VISIBLE, under the label — not a title
                  tooltip. It is the only written record of the rule behind
                  this rate, so it has to survive a screenshot and a screen
                  reader alike; aria-describedby is what ties it to the box. */}
              {f.hint ? (
                <p id={hintId} style={{ margin: '0 0 6px', fontSize: '12.5px', lineHeight: 1.45, color: 'var(--gps-muted)' }}>
                  {f.hint}
                </p>
              ) : null}
              <TextInput
                id={inputId}
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                aria-describedby={f.hint ? hintId : undefined}
                value={rates[f.field]}
                onChange={(e) => setRates((prev) => ({ ...prev, [f.field]: e.target.value }))}
                style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              />
            </Field>
          );
        })}

        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginTop: 'var(--gps-s-2)' }}>
          <Button type="submit" disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            {busy ? 'Saving…' : 'Save rates'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy} style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', fontSize: '14.5px' }}>
            Cancel
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
